import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu as MenuIcon, Phone, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { AuthMenu } from "@/components/AuthMenu";
import { getStatus, scrollToHash } from "@/utils/hours";
import { INFO } from "@/data/content";

const NAV = [
  { href: "#storia", label: "Chi Siamo", testid: "nav-link-chi-siamo" },
  { href: "#menu", label: "Menu", testid: "nav-link-menu" },
  { href: "#recensioni", label: "Recensioni", testid: "nav-link-recensioni" },
  { href: "#galleria", label: "Galleria", testid: "nav-link-galleria" },
  { href: "#contatti", label: "Contatti", testid: "nav-link-contatti" },
];

export const Header = ({ user, onLogout }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(() => getStatus());

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    const t = setInterval(() => setStatus(getStatus()), 60_000);
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearInterval(t);
    };
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToHash(href);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled || open
            ? "bg-espresso/85 backdrop-blur-xl border-b border-sand/15"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8">
          <a
            href="#top"
            data-testid="bar-incontro-logo"
            onClick={(e) => go(e, "#top")}
            aria-label="Bar Incontro — home"
          >
            <Logo />
          </a>

          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={n.testid}
                onClick={(e) => go(e, n.href)}
                className="text-sm font-medium text-cream/75 transition-colors duration-300 hover:text-cream"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <span
              data-testid="live-status-badge"
              className="inline-flex items-center gap-2 rounded-full border border-sand/20 px-3 py-1.5 text-xs text-cream/80"
            >
              <span
                className={`size-1.5 rounded-full ${
                  status.open ? "bg-[#2ECC71]" : "bg-[#E74C3C]"
                }`}
              />
              {status.open ? "Aperto" : "Chiuso"}
            </span>
            <AuthMenu user={user} onLogout={onLogout} />
            <a
              href={INFO.phoneHref}
              data-testid="header-call-button"
              className="inline-flex items-center gap-2 rounded-full bg-terra px-5 py-2.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-terra-light"
            >
              <Phone className="size-4" />
              {INFO.phoneDisplay}
            </a>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            className="inline-flex size-10 items-center justify-center rounded-full border border-sand/20 text-cream lg:hidden"
          >
            {open ? <X className="size-5" /> : <MenuIcon className="size-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 flex flex-col justify-between bg-espresso/98 px-6 pb-10 pt-28 backdrop-blur-xl lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {NAV.map((n, i) => (
                <motion.a
                  key={n.href}
                  href={n.href}
                  data-testid={n.testid}
                  onClick={(e) => go(e, n.href)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="border-b border-sand/10 py-4 font-serif text-4xl font-medium text-cream"
                >
                  {n.label}
                </motion.a>
              ))}
            </nav>
            <div className="flex flex-col gap-4">
              <span
                data-testid="live-status-badge"
                className="inline-flex w-fit items-center gap-2 rounded-full border border-sand/20 px-3 py-1.5 text-xs text-cream/80"
              >
                <span
                  className={`size-1.5 rounded-full ${
                    status.open ? "bg-[#2ECC71]" : "bg-[#E74C3C]"
                  }`}
                />
                {status.text}
              </span>
              <AuthMenu user={user} onLogout={onLogout} showName={false} />
              <a
                href={INFO.phoneHref}
                data-testid="header-call-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-terra px-6 py-3.5 font-semibold text-cream"
              >
                <Phone className="size-4" />
                {INFO.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
